import React, { memo, useMemo, useRef, useEffect } from 'react';
import * as THREE from 'three';

// BoxInstances component: renders all boxes for a pallet using InstancedMesh
const BoxInstances = memo(function BoxInstances({
  totalBoxes,
  boxesOriginX,
  boxesOriginZ,
  topDeckY,
  deckThickness,
  box,
  boxes,
  colors,
  opacity
}) {
  const meshRef = useRef();
  
  // Pre-compute all box transformation matrices
  useEffect(() => {
    if (!meshRef.current) return;
    
    const temp = new THREE.Object3D();
    let idx = 0;
    
    for (let layer = 0; layer < boxes.layers; layer++) {
      for (let r = 0; r < boxes.rows; r++) {
        for (let c = 0; c < boxes.cols; c++) {
          const x = boxesOriginX + c * (box.width + boxes.gap);
          const z = boxesOriginZ + r * (box.length + boxes.gap);
          const y = topDeckY + deckThickness / 2 + box.height / 2 + layer * (box.height + 0.2);
          
          temp.position.set(x, y, z);
          temp.updateMatrix();
          meshRef.current.setMatrixAt(idx, temp.matrix);
          idx++;
        }
      }
    }
    
    meshRef.current.instanceMatrix.needsUpdate = true;
  }, [boxesOriginX, boxesOriginZ, topDeckY, deckThickness, box, boxes, totalBoxes]);
  
  return (
    <instancedMesh ref={meshRef} args={[null, null, totalBoxes]} castShadow receiveShadow>
      <boxGeometry args={[box.width, box.height, box.length]} />
      <meshStandardMaterial color={colors.box} opacity={opacity} transparent={opacity < 1} />
    </instancedMesh>
  );
});

/*
  PalletStack renders a Four-Way Entry Wing Type pallet with notched stringers
  and a stack of cardboard boxes on top.

  External API preserved (do not break callers):
  - position: [x, y, z]
  - pallet: { width, length, height }
  - box: { width, length, height }
  - boxes: { cols, rows, layers, gap }
  - colors: { wood, box }
  - opacity

  Implementation details:
  - Top deck boards (multiple) running along Z with small gaps, with slight overhang (wings) beyond stringers.
  - Bottom deck boards (fewer) running along Z.
  - Three stringers running along Z with centered notches (simulated by splitting into segments), enabling fork entry on all four sides.
*/

const PalletStack = memo(function PalletStack({
  position = [0, 0, 0],
  pallet = { width: 18, length: 22, height: 2.5 },
  box = { width: 8, length: 10, height: 6 },
  boxes = { cols: 2, rows: 2, layers: 1, gap: 0.5 },
  colors = { wood: '#8b5a2b', box: '#DCC488' },
  opacity = 1,
  variant = 'full', // 'full' or 'lite'
}) {
  const [x0, y0, z0] = position;

  // Four-way wing pallet parameters
  const deckThickness = 0.55;     // thickness of top/bottom deck boards (slightly reduced)
  const bottomDeckThickness = 0.55;
  const stringerHeight = 0.2;     // height of stringers (slightly reduced)
  const stringerWidth = 2.2;     // thickness across X of each stringer
  const overhang = 1.5;          // wing overhang beyond stringer pack on both sides (X)
  const topBoardCount = 7;       // number of top deck boards
  const bottomBoardCount = 3;    // number of bottom deck boards
  const boardGap = 0.2;          // gap between adjacent deck boards (X)
  const notchLength = 6.0;       // length of side notches along Z (gap in stringer)
  const notchGapZ = notchLength; // shorthand

  // Inner pack width for stringers (excluding wings)
  const innerW = Math.max(1, pallet.width - 2 * overhang);
  const innerHalf = innerW / 2;

  // Elevations (Y)
  const bottomDeckY = y0 + bottomDeckThickness / 2; // sits on floor
  const stringerY = bottomDeckY + bottomDeckThickness / 2 + stringerHeight / 2; // on top of bottom deck
  const topDeckY = stringerY + stringerHeight / 2 + deckThickness / 2; // on top of stringers

  // Compute box grid extents on pallet (centered on pallet frame)
  const totalBoxesW = boxes.cols * box.width + (boxes.cols - 1) * boxes.gap;
  const totalBoxesL = boxes.rows * box.length + (boxes.rows - 1) * boxes.gap;

  // Center the boxes on the pallet
  const boxesOriginX = x0 - totalBoxesW / 2 + box.width / 2;
  const boxesOriginZ = z0 - totalBoxesL / 2 + box.length / 2;

  // Lite variant: render a single pallet block and a subdivided cardboard-box grid per layer
  if (variant === 'full') {
    const palletTopY = y0 + pallet.height; // simple block height
    const baseBoxY = palletTopY + box.height / 2; // sit directly on pallet without extra lift

    const boxColumns = boxes.cols ?? 2;
    const boxRows = boxes.rows ?? 2;
    const horizontalGap = boxes.gap ?? 0.2;
    const depthGap = boxes.gap ?? 0.2;
    const horizontalMargin = 0.4; // total margin across pallet width
    const depthMargin = 0.4;      // total margin across pallet length

    const availableWidth = Math.max(0.1, pallet.width - horizontalMargin);
    const availableLength = Math.max(0.1, pallet.length - depthMargin);

    const individualBoxWidth = Math.max(
      0.1,
      (availableWidth - (boxColumns - 1) * horizontalGap) / boxColumns
    );
    const individualBoxLength = Math.max(
      0.1,
      (availableLength - (boxRows - 1) * depthGap) / boxRows
    );

    const totalBoxesW = boxColumns * individualBoxWidth + (boxColumns - 1) * horizontalGap;
    const totalBoxesL = boxRows * individualBoxLength + (boxRows - 1) * depthGap;
    const originX = x0 - totalBoxesW / 2 + individualBoxWidth / 2;
    const originZ = z0 - totalBoxesL / 2 + individualBoxLength / 2;

    return (
      <group position={[0, 0, 0]}>
        {/* Simplified pallet as a single box */}
        <mesh position={[x0, y0 + pallet.height / 2, z0]}>
          <boxGeometry args={[pallet.width, pallet.height, pallet.length]} />
          <meshStandardMaterial color={colors.wood} opacity={opacity} transparent={opacity < 1} />
        </mesh>

        {/* Subdivided cardboard-box grid: rows × columns per pallet layer */}
        {Array.from({ length: boxes.layers }).flatMap((_, layer) => {
          const y = baseBoxY + layer * (box.height + 0.1);
          return Array.from({ length: boxRows }).flatMap((_, r) =>
            Array.from({ length: boxColumns }).map((_, c) => {
              const x = originX + c * (individualBoxWidth + horizontalGap);
              const z = originZ + r * (individualBoxLength + depthGap);
              return (
                <mesh
                  key={`lite-box-${layer}-${r}-${c}`}
                  position={[x, y, z]}
                >
                  <boxGeometry args={[individualBoxWidth, box.height, individualBoxLength]} />
                  <meshStandardMaterial color={colors.box} opacity={opacity} transparent={opacity < 1} />
                </mesh>
              );
            })
          );
        })}
      </group>
    );
  }

  return (
    <group position={[0, 0, 0]}>
      {/* Bottom deck boards (fewer), running along Z across full pallet width (wings visible) */}
      {Array.from({ length: bottomBoardCount }).map((_, i) => {
        const totalGap = (bottomBoardCount - 1) * boardGap;
        const boardW = (pallet.width - totalGap) / bottomBoardCount;
        const x = x0 - pallet.width / 2 + (i * (boardW + boardGap)) + boardW / 2;
        return (
          <mesh key={`bottom-board-${i}`} position={[x, bottomDeckY, z0]} castShadow receiveShadow>
            <boxGeometry args={[boardW, bottomDeckThickness, pallet.length]} />
            <meshStandardMaterial color={colors.wood} opacity={opacity} transparent={opacity < 1} />
          </mesh>
        );
      })}

      {/* Three stringers along Z (under top deck) with central notches (simulated by splitting) */}
      {[-1, 0, 1].map((ix, idx) => {
        const sx = x0 + ix * (innerHalf - stringerWidth / 2);
        const halfL = pallet.length / 2;
        const segL = (pallet.length - notchGapZ) / 2;
        const zFrontSeg = z0 + (halfL - segL / 2);
        const zBackSeg = z0 - (halfL - segL / 2);
        return (
          <group key={`stringer-${idx}`}>
            {/* front segment */}
            <mesh position={[sx, stringerY, zFrontSeg]} castShadow receiveShadow>
              <boxGeometry args={[stringerWidth, stringerHeight, segL]} />
              <meshStandardMaterial color={colors.wood} opacity={opacity} transparent={opacity < 1} />
            </mesh>
            {/* back segment */}
            <mesh position={[sx, stringerY, zBackSeg]} castShadow receiveShadow>
              <boxGeometry args={[stringerWidth, stringerHeight, segL]} />
              <meshStandardMaterial color={colors.wood} opacity={opacity} transparent={opacity < 1} />
            </mesh>
          </group>
        );
      })}

      {/* Top deck boards (many), running along Z across full pallet width with gaps */}
      {Array.from({ length: topBoardCount }).map((_, i) => {
        const totalGap = (topBoardCount - 1) * boardGap;
        const boardW = (pallet.width - totalGap) / topBoardCount;
        const x = x0 - pallet.width / 2 + (i * (boardW + boardGap)) + boardW / 2;
        return (
          <mesh key={`top-board-${i}`} position={[x, topDeckY, z0]} castShadow receiveShadow>
            <boxGeometry args={[boardW, deckThickness, pallet.length]} />
            <meshStandardMaterial color={colors.wood} opacity={opacity} transparent={opacity < 1} />
          </mesh>
        );
      })}

      <BoxInstances
        totalBoxes={boxes.layers * boxes.rows * boxes.cols}
        boxesOriginX={boxesOriginX}
        boxesOriginZ={boxesOriginZ}
        topDeckY={topDeckY}
        deckThickness={deckThickness}
        box={box}
        boxes={boxes}
        colors={colors}
        opacity={opacity}
      />
    </group>
  );
});

export default PalletStack;
